import { Component, computed, inject, input, resource, signal } from "@angular/core";
import { Apollo } from "apollo-angular";
import { CertChartComponent } from "../../../../components/cert/cert-chart/cert-chart";
import { CertFilter } from "../../../../components/cert/cert-filter/cert-filter";
import { CertTableComponent } from "../../../../components/cert/cert-table/cert-table";
import { AirtScanConnection, AirtSort, SearchFilter } from "../../../../gql/generated";
import { AIRT_SCAN_QUERY } from "../../../../graphql/domain/airtscan";
import { Sort } from "@angular/material/sort";
import { TranslateModule } from "@ngx-translate/core";

const EMPTY_PAGE_INFO = {
  filterFields: [],
  hasNextPage: false,
  hasPreviousPage: false,
  sortFields: [],
  endCursor: 0,
  startCursor: 0,
  totalItems: 0,
};

@Component({
  selector: "scan-tab",
  templateUrl: "scan-tab.html",
  styleUrls: ["../../cert.scss"],
  imports: [CertChartComponent, CertFilter, CertTableComponent, TranslateModule],
})
export class ScanTab {
  private apollo = inject(Apollo);

  public prefixes = input.required<string[]>();
  public airtScanResults = resource({
    request: () => ({
      filter: this.searchFilter(),
      paginationAfter: this.paginationAfter(),
      sort: this.querySort(),
    }),
    loader: async ({ request }): Promise<AirtScanConnection> => {
      const { data } = await this.fetchAirtScanInformation(request);
      return data.airtScans;
    },
  });

  public airtScans = computed(() => {
    if (!this.airtScanResults.hasValue()) {
      return [];
    }
    return this.airtScanResults.value().edges;
  });
  public pageInfo = computed(() =>
    this.airtScanResults.hasValue() ? (this.airtScanResults.value().pageInfo ?? EMPTY_PAGE_INFO) : EMPTY_PAGE_INFO,
  );
  private querySort = computed(() => ({
    order: [{ [this.sortInfo().active || "ip"]: this.sortInfo().direction.toUpperCase() || "ASC" }],
  }));

  public searchFilter = signal<SearchFilter>({});
  private paginationAfter = signal(0);
  private sortInfo = signal<Sort>({
    active: "ip",
    direction: "asc",
  });

  fetchAirtScanInformation(args: { filter: SearchFilter; paginationAfter: number; sort: AirtSort }) {
    return this.apollo
      .use("replicatedData")
      .query<{ airtScans: AirtScanConnection }>({
        query: AIRT_SCAN_QUERY,
        fetchPolicy: "network-only",
        errorPolicy: "all",
        variables: {
          filter: args.filter,
          first: 20,
          after: args.paginationAfter,
          sort: args.sort,
        },
      })
      .toPromise();
  }

  onFiltersChanged(event: any) {
    this.searchFilter.set(event);
  }

  onPaginationChanged(event: number) {
    this.paginationAfter.set(event > -1 ? event : 0);
  }

  onSortingChanged(event: Sort) {
    this.sortInfo.set(event);
  }
}
